import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Smartphone, 
  Plus,
  Trash2,
  Upload,
  Globe,
  Apple,
  Play
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platforms = ['mobile', 'web', 'both'];

export default function AdminApps() {
  const { currentTheme } = useTheme();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    platform: '',
    app_store_url: '',
    play_store_url: '',
    web_url: '',
    icon: null
  });

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/apps`);
      setApps(response.data || []);
    } catch (error) {
      console.error('Error fetching apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.icon) {
      toast.error('Please select an icon');
      return;
    }

    setCreating(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('platform', formData.platform);
      data.append('app_store_url', formData.app_store_url);
      data.append('play_store_url', formData.play_store_url);
      data.append('web_url', formData.web_url);
      data.append('icon', formData.icon);

      await axios.post(`${API}/admin/apps`, data, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('App added successfully!');
      setShowDialog(false);
      setFormData({
        name: '',
        description: '',
        platform: '',
        app_store_url: '',
        play_store_url: '',
        web_url: '',
        icon: null
      });
      fetchApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add app');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this app?')) return;

    try {
      await axios.delete(`${API}/admin/apps/${appId}`, { withCredentials: true });
      toast.success('App deleted');
      fetchApps();
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  return (
    <AdminLayout title="Apps Management">
      <div className="flex justify-between items-center mb-6">
        <p className="font-body opacity-70">
          Showcase your mobile and web applications.
        </p>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: currentTheme.primary }} data-testid="add-app-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add App
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Add New App</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>App Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Awesome App"
                  required
                  data-testid="app-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your app..."
                  rows={3}
                  required
                  data-testid="app-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Platform</Label>
                <Select 
                  value={formData.platform} 
                  onValueChange={(v) => setFormData({ ...formData, platform: v })}
                >
                  <SelectTrigger data-testid="app-platform-select">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map((p) => (
                      <SelectItem key={p} value={p} className="capitalize">
                        {p === 'both' ? 'Mobile & Web' : p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(formData.platform === 'mobile' || formData.platform === 'both') && (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Apple className="w-4 h-4" />
                      App Store URL
                    </Label>
                    <Input
                      value={formData.app_store_url}
                      onChange={(e) => setFormData({ ...formData, app_store_url: e.target.value })}
                      placeholder="https://apps.apple.com/..."
                      data-testid="app-store-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      Play Store URL
                    </Label>
                    <Input
                      value={formData.play_store_url}
                      onChange={(e) => setFormData({ ...formData, play_store_url: e.target.value })}
                      placeholder="https://play.google.com/..."
                      data-testid="play-store-url-input"
                    />
                  </div>
                </>
              )}

              {(formData.platform === 'web' || formData.platform === 'both') && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Web URL
                  </Label>
                  <Input
                    value={formData.web_url}
                    onChange={(e) => setFormData({ ...formData, web_url: e.target.value })}
                    placeholder="https://myapp.com"
                    data-testid="web-url-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>App Icon</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, icon: e.target.files[0] })}
                  required
                  data-testid="app-icon-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{ backgroundColor: currentTheme.primary }}
                disabled={creating}
                data-testid="create-app-btn"
              >
                {creating ? 'Adding...' : 'Add App'}
                <Upload className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-64 rounded-xl" />
          ))}
        </div>
      ) : apps.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => (
            <div
              key={app.app_id}
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
              data-testid={`app-card-${app.app_id}`}
            >
              <div 
                className="p-6 flex items-center gap-4"
                style={{ background: `linear-gradient(135deg, ${currentTheme.primary}10, ${currentTheme.accent}10)` }}
              >
                <img
                  src={`${BACKEND_URL}${app.icon_url}`}
                  alt={app.name}
                  className="w-16 h-16 rounded-xl object-cover"
                />
                <div>
                  <h3 className="font-body font-medium text-lg">{app.name}</h3>
                  <span 
                    className="text-sm capitalize opacity-70"
                  >
                    {app.platform === 'both' ? 'Mobile & Web' : app.platform}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <p className="font-body text-sm opacity-70 line-clamp-2 mb-4">
                  {app.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {app.app_store_url && (
                      <a href={app.app_store_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">
                          <Apple className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                    {app.play_store_url && (
                      <a href={app.play_store_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">
                          <Play className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                    {app.web_url && (
                      <a href={app.web_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">
                          <Globe className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(app.app_id)}
                    data-testid={`delete-app-${app.app_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Smartphone className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
            No apps yet
          </h3>
          <p className="font-body opacity-60">
            Add your first app to showcase.
          </p>
        </div>
      )}
    </AdminLayout>
  );
}
